// src/views/WorkQueueView.js
// ---------------------------------------------------------------------------
// COLA DE TRABAJO POR ESTACIÓN (S3).
//
// Pestañas = estación. Columnas = las máquinas de esa estación, leídas de
// `machines` (nunca hardcodeadas: si Daniel crea una máquina, aparece sola).
// Arrastrar en horizontal cambia `machineId`; en vertical, `order`.
//
// La pestaña "Todas" es un panorama de 4 columnas, una por estación, y es
// READ-ONLY a propósito: ahí una ficha no tiene una máquina destino evidente.
//
// Tema light y `var(--…)` de tokens.css donde hay color propio (D1 del handoff).
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, doc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { LayoutGrid, AlertTriangle, Inbox, Plus, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TaskKanbanBoard from '../components/JobView/Tabs/TaskKanbanBoard';
import { cutBeforePrintFor } from '../lib/fichaSheets.js';
import {
    STATIONS, STATION_LABEL, machinesForStation, sortQueue, reorderColumn,
    SOLICITUD_STATION, SOLICITUD_STAGES, normalizeStage, isSolicitud, canConvertSolicitud,
} from '../lib/workQueue.js';
import { createJob } from '../lib/jobs.js';
import { SolicitudFormModal, ConvertirModal } from '../components/Queue/SolicitudModal.js';
import FichaModal from '../components/Queue/FichaModal.js';

const SIN_MAQUINA = '__sin_maquina__';

const STATUS_CHIP = {
    queued: { label: 'En cola', cls: 'bg-gray-100 text-gray-600' },
    in_progress: { label: 'En curso', cls: 'bg-blue-100 text-blue-700' },
    done: { label: 'Hecho', cls: 'bg-green-100 text-green-700' },
    stalled: { label: 'Estancada', cls: 'bg-red-100 text-red-700' },
};

// Tarjeta: portada, jobNumber, nombre, cliente, barra de avance, chip de máquina
// y flag stalled. La portada y el nombre NO viven en la ficha: se resuelven contra
// `jobs` en la vista (mismo campo `coverImageUrl` que la columna Preview del
// dashboard), para no denormalizarlos en `work_queue`.
const QueueCard = ({ ficha, job, machineName, onOpen }) => {
    const total = Number(ficha.sheetsTotal) || 0;
    const hechas = Number(ficha.sheetsDone) || 0;
    const pct = total > 0 ? Math.round((hechas / total) * 100) : 0;
    const chip = STATUS_CHIP[ficha.status] || STATUS_CHIP.queued;
    return (
        <div
            data-testid={`ficha-${ficha.id}`}
            onClick={() => onOpen(ficha.jobId)}
            className={`bg-white rounded-md p-3 shadow-sm border-l-4 hover:shadow-md transition-shadow cursor-pointer ${ficha.status === 'stalled' ? 'border-red-500' : ficha.status === 'done' ? 'border-green-500' : 'border-blue-400'}`}
        >
            <div className="flex gap-2">
                {/* 48x48, con el mismo respaldo gris del dashboard cuando no hay portada.
                    `draggable={false}`: sin eso el arrastre nativo de la imagen le roba
                    el gesto a react-beautiful-dnd. */}
                {job && job.coverImageUrl ? (
                    <img
                        src={job.coverImageUrl}
                        alt=""
                        draggable={false}
                        className="w-12 h-12 flex-shrink-0 object-cover rounded-md bg-gray-100"
                    />
                ) : (
                    <div className="w-12 h-12 flex-shrink-0 rounded-md bg-gray-100 flex items-center justify-center text-gray-300 text-xs">—</div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-gray-800 font-mono text-sm">{ficha.jobNumber || ficha.jobId}</span>
                        {ficha.status === 'stalled' && (
                            <span title="Sin escaneo en más de 48 h" className="text-red-600 flex-shrink-0"><AlertTriangle size={14} /></span>
                        )}
                    </div>
                    {job && job.jobName && (
                        <p className="text-xs text-gray-700 truncate" title={job.jobName}>{job.jobName}</p>
                    )}
                    <p className="text-xs text-gray-500 truncate" title={ficha.clientName}>{ficha.clientName || 'Sin cliente'}</p>
                </div>
            </div>

            <div className="mt-2 flex items-center gap-2">
                <span className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <span className={`block h-full ${ficha.status === 'done' ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </span>
                <span className="font-mono text-[11px] text-gray-600 tabular-nums flex-shrink-0">{hechas}/{total}</span>
            </div>

            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 truncate max-w-[140px]" title={machineName}>
                    {machineName || 'Sin máquina'}
                </span>
            </div>
        </div>
    );
};

// Tarjeta de una SOLICITUD: imagen, nombre, cliente y nota. Sin barra ni chip
// de máquina (no aplica). El botón de convertir solo aparece en la columna
// Lista y solo para quien puede gestionar la cola.
const SolicitudCard = ({ ficha, canManage, onOpenConvert }) => (
    <div
        data-testid={`ficha-${ficha.id}`}
        className={`bg-white rounded-md p-3 shadow-sm border-l-4 hover:shadow-md transition-shadow ${ficha.status === 'done' ? 'border-green-500 opacity-60' : 'border-amber-400'}`}
    >
        <div className="flex gap-2">
            {ficha.imageUrl ? (
                <img src={ficha.imageUrl} alt="" draggable={false}
                    className="w-12 h-12 flex-shrink-0 object-cover rounded-md bg-gray-100" />
            ) : (
                <div className="w-12 h-12 flex-shrink-0 rounded-md bg-gray-100 flex items-center justify-center text-gray-300 text-xs">—</div>
            )}
            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-800 truncate" title={ficha.jobName}>{ficha.jobName || '(sin nombre)'}</p>
                <p className="text-xs text-gray-500 truncate" title={ficha.clientName}>{ficha.clientName || 'Sin cliente'}</p>
                {ficha.note && <p className="text-[11px] text-gray-400 truncate" title={ficha.note}>{ficha.note}</p>}
            </div>
        </div>
        {ficha.status === 'done' && ficha.jobId && (
            <p className="mt-2 text-[11px] font-semibold text-green-700">✓ Convertida en {ficha.jobId}</p>
        )}
        {canConvertSolicitud(ficha) && canManage && (
            <button
                onClick={(e) => { e.stopPropagation(); onOpenConvert(ficha); }}
                className="mt-2 w-full py-1.5 text-xs font-bold bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center gap-1.5"
            >
                <Briefcase size={13} /> Convertir en trabajo
            </button>
        )}
    </div>
);

const WorkQueueView = ({ db, storage, user }) => {
    const [fichas, setFichas] = useState([]);
    const [machines, setMachines] = useState([]);
    // Solo para la portada y el nombre de la tarjeta. La ficha no los guarda.
    const [jobsById, setJobsById] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [tab, setTab] = useState('IMPRESION');
    const [clients, setClients] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [convertir, setConvertir] = useState(null);   // solicitud elegida
    const [converting, setConverting] = useState(false);
    // Sesión A: la ficha de IMPRESIÓN/CORTE se abre en un modal con las planchas y
    // el avance por plancha (D14: el operador no vuelve a entrar a prepress). El
    // job completo sigue a un toque ("Abrir trabajo").
    const [fichaAbierta, setFichaAbierta] = useState(null);
    // Gate del tablero: VER es canViewDashboard (la ruta ya lo exige);
    // MOVER y CONVERTIR exigen canManageQueue — mismo criterio que las rules.
    const canManage = !!user?.permissions?.canManageQueue;
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!db) return undefined;
        const unsubFichas = onSnapshot(collection(db, 'work_queue'), (snap) => {
            setFichas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        }, (e) => {
            console.error('Error leyendo work_queue:', e);
            setError('No se pudo cargar la cola. ¿Tienes permiso para ver el dashboard?');
            setIsLoading(false);
        });
        const unsubMachines = onSnapshot(collection(db, 'machines'), (snap) => {
            setMachines(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }, (e) => console.error('Error leyendo machines:', e));
        const unsubJobs = onSnapshot(collection(db, 'jobs'), (snap) => {
            const m = {};
            snap.docs.forEach((d) => { const j = d.data(); m[d.id] = { coverImageUrl: j.coverImageUrl, jobName: j.jobName }; });
            setJobsById(m);
        // Sin `jobs` la cola sigue sirviendo: la tarjeta cae al respaldo gris.
        }, (e) => console.error('Error leyendo jobs:', e));
        // Clientes: para el selector del formulario de solicitud.
        const unsubClients = onSnapshot(collection(db, 'clients'), (snap) => {
            setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }, (e) => console.error('Error leyendo clients:', e));
        return () => { unsubFichas(); unsubMachines(); unsubJobs(); unsubClients(); };
    }, [db]);

    const machineName = useCallback(
        (id) => machines.find((m) => m.id === id)?.name || '',
        [machines],
    );

    // Columnas de la pestaña activa: las máquinas de esa estación + "Sin máquina",
    // que es donde nacen las fichas y de donde el humano las saca.
    const lanes = useMemo(() => {
        if (tab === 'TODAS') {
            return STATIONS.map((st) => ({ key: st, label: STATION_LABEL[st] }));
        }
        // SOLICITUDES: columnas fijas por etapa, no por máquina.
        if (tab === SOLICITUD_STATION) {
            return SOLICITUD_STAGES.map((e) => ({ key: e.key, label: e.label, dot: e.dot }));
        }
        return [
            { key: SIN_MAQUINA, label: 'Sin asignar', dot: 'bg-gray-300' },
            ...machinesForStation(machines, tab).map((m) => ({ key: m.id, label: m.name, dot: 'bg-blue-400' })),
        ];
    }, [machines, tab]);

    const fichasVisibles = useMemo(() => {
        // "Todas" es el panorama de PRODUCCIÓN: las solicitudes no entran ahí,
        // tienen su pestaña. En SOLICITUDES, las convertidas (done) se quedan
        // visibles en su columna como recibo de la conversión.
        const base = tab === 'TODAS'
            ? fichas.filter((f) => !isSolicitud(f))
            : fichas.filter((f) => f.station === tab);
        return sortQueue(base);
    }, [fichas, tab]);

    const carrilDe = useCallback(
        (f) => (tab === 'TODAS' ? f.station
            : tab === SOLICITUD_STATION ? normalizeStage(f.stage)
            : (f.machineId || SIN_MAQUINA)),
        [tab],
    );

    // Horizontal → machineId. Vertical → order. Todo en UN batch: si dos personas
    // arrastran a la vez, la última gana entera, no a medias.
    const handleMove = useCallback(async (fichaId, laneKey, destIndex) => {
        if (tab === 'TODAS') return;                       // panorama read-only
        // El drag está deshabilitado sin canManageQueue; esta guarda es el
        // cinturón por si algo lo dispara igual. Las rules cierran del lado servidor.
        if (!canManage) return;
        const ficha = fichas.find((f) => f.id === fichaId);
        if (!ficha) return;

        // SOLICITUDES: el carril es la ETAPA, no la máquina.
        if (tab === SOLICITUD_STATION) {
            const enDestino = sortQueue(
                fichas.filter((f) => isSolicitud(f) && normalizeStage(f.stage) === laneKey && f.id !== fichaId),
            );
            const cambios = reorderColumn([...enDestino, { ...ficha, stage: laneKey }], fichaId, destIndex);
            try {
                const batch = writeBatch(db);
                if (normalizeStage(ficha.stage) !== laneKey) {
                    batch.update(doc(db, 'work_queue', fichaId), { stage: laneKey });
                }
                cambios.forEach((c) => batch.update(doc(db, 'work_queue', c.id), { order: c.order }));
                await batch.commit();
            } catch (e) {
                console.error('Error moviendo la solicitud:', e);
                setError('No se pudo mover la solicitud. ¿Tienes permiso para gestionar la cola?');
            }
            return;
        }

        const nuevaMaquina = laneKey === SIN_MAQUINA ? null : laneKey;

        // La columna destino tal como quedará, para reindexar `order`.
        const enDestino = sortQueue(
            fichas.filter((f) => f.station === ficha.station
                && (f.machineId || SIN_MAQUINA) === laneKey
                && f.id !== fichaId),
        );
        const conMovida = [...enDestino, { ...ficha, machineId: nuevaMaquina }];
        const cambios = reorderColumn(conMovida, fichaId, destIndex);

        try {
            const batch = writeBatch(db);
            if ((ficha.machineId || null) !== nuevaMaquina) {
                batch.update(doc(db, 'work_queue', fichaId), { machineId: nuevaMaquina });
            }
            cambios.forEach((c) => batch.update(doc(db, 'work_queue', c.id), { order: c.order }));
            await batch.commit();
        } catch (e) {
            console.error('Error moviendo la ficha:', e);
            setError('No se pudo mover la ficha. Revisa tu conexión.');
        }
    }, [db, fichas, tab, canManage]);

    const abrirJob = useCallback((jobId) => navigate(`/jobs/${jobId}`), [navigate]);
    const abrirFicha = useCallback((fichaId) => setFichaAbierta(fichas.find((f) => f.id === fichaId) || null), [fichas]);

    // Convertir solicitud -> trabajo. Reusa createJob (numeración atómica,
    // forma canónica del doc) con quick:true y SIN planchas; la imagen de la
    // solicitud pasa a ser el preview del job (misma URL, sin re-subir).
    const handleConvert = useCallback(async (solicitud, station) => {
        if (!canManage || !canConvertSolicitud(solicitud)) return;
        setConverting(true);
        try {
            const jobId = await createJob(db, {
                clientId: solicitud.clientId,
                jobName: solicitud.jobName,
                description: solicitud.note || '',
                quick: true,
                createdVia: 'solicitud',
            }, user);
            await updateDoc(doc(db, 'jobs', jobId), { coverImageUrl: solicitud.imageUrl || '' });
            // Ficha en la estación elegida, con el id determinista de S3: si el
            // job luego gana planchas, el trigger de avance la encuentra sola.
            await setDoc(doc(db, 'work_queue', `${jobId}__${station}`), {
                jobId,
                jobNumber: Number(jobId.replace(/^J-/, '')) || null,
                clientName: solicitud.clientName || '',
                station,
                machineId: null,
                order: 0,
                status: 'queued',
                sheetsTotal: 0,
                sheetsDone: 0,
                lastScanAt: null,
                createdAt: new Date(),
                createdBy: 'convert:' + (user?.uid || ''),
            });
            await updateDoc(doc(db, 'work_queue', solicitud.id), { status: 'done', jobId });
            setConvertir(null);
            navigate(`/jobs/${jobId}`);
        } catch (e) {
            console.error('Error convirtiendo la solicitud:', e);
            setError('No se pudo convertir: ' + (e.message || ''));
        } finally {
            setConverting(false);
        }
    }, [db, user, canManage, navigate]);

    const renderCard = useCallback(
        (f) => (isSolicitud(f)
            ? <SolicitudCard ficha={f} canManage={canManage} onOpenConvert={setConvertir} />
            : <QueueCard ficha={f} job={jobsById[f.jobId]} machineName={machineName(f.machineId)} onOpen={() => abrirFicha(f.id)} />),
        [machineName, abrirFicha, canManage, jobsById],
    );

    const conteo = useCallback(
        (st) => fichas.filter((f) => f.station === st).length,
        [fichas],
    );

    return (
        <div className="p-4 bg-gray-50 flex flex-col md:h-full md:overflow-hidden">
            <header className="flex justify-between items-center mb-4 flex-shrink-0 gap-3">
                <h1 className="text-3xl font-bold text-gray-800">Cola de Trabajo</h1>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">{fichas.length} ficha{fichas.length === 1 ? '' : 's'}</span>
                    {/* Crear solicitud: abierto a cualquiera que VE la cola (las rules
                        permiten create con canViewDashboard). */}
                    <button
                        onClick={() => setFormOpen(true)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg shadow hover:bg-blue-700"
                    >
                        <Plus size={16} /> Solicitud
                    </button>
                </div>
            </header>

            {/* Pestañas por estación + panorama */}
            <div className="flex gap-2 mb-4 overflow-x-auto flex-shrink-0 pb-1">
                <button
                    onClick={() => setTab(SOLICITUD_STATION)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${tab === SOLICITUD_STATION ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}
                >
                    Solicitudes <span className={tab === SOLICITUD_STATION ? 'text-white/70' : 'text-gray-400'}>({fichas.filter(isSolicitud).length})</span>
                </button>
                {STATIONS.map((st) => (
                    <button
                        key={st}
                        onClick={() => setTab(st)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${tab === st ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}
                    >
                        {STATION_LABEL[st]} <span className={tab === st ? 'text-white/70' : 'text-gray-400'}>({conteo(st)})</span>
                    </button>
                ))}
                <button
                    onClick={() => setTab('TODAS')}
                    title="Panorama de las 4 estaciones — solo lectura"
                    className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${tab === 'TODAS' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}
                >
                    <LayoutGrid size={15} /> Todas
                </button>
            </div>

            {error && <p className="mb-3 text-sm text-red-600 flex-shrink-0">{error}</p>}

            {tab === 'TODAS' && (
                <p className="mb-2 text-xs text-gray-500 flex-shrink-0">
                    Panorama de solo lectura. Para mover una ficha, entra a la pestaña de su estación.
                </p>
            )}
            {tab !== 'TODAS' && !canManage && (
                <p className="mb-2 text-xs text-gray-500 flex-shrink-0">
                    Puedes ver la cola y crear solicitudes. Mover fichas requiere el permiso de gestión.
                </p>
            )}

            <div className="min-h-[70vh] md:min-h-0 md:flex-grow">
                {isLoading ? (
                    <p className="text-center py-10 text-gray-500">Cargando la cola…</p>
                ) : fichas.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500">
                        <Inbox size={32} className="mx-auto mb-3 text-gray-300" />
                        <p className="font-semibold">No hay fichas todavía.</p>
                        <p className="text-sm mt-1">Se crean solas cuando un trabajo entra en “En Progreso” o “Pendiente”.</p>
                    </div>
                ) : (
                    <TaskKanbanBoard
                        title={tab === 'TODAS' ? 'Panorama por estación'
                            : tab === SOLICITUD_STATION ? 'Solicitudes · por etapa'
                            : `${STATION_LABEL[tab]} · por máquina`}
                        icon={<LayoutGrid className="text-blue-500" />}
                        tasks={fichasVisibles}
                        lanes={lanes}
                        laneOf={carrilDe}
                        onMove={handleMove}
                        dragDisabled={tab === 'TODAS' || !canManage}
                        renderCard={renderCard}
                        emptyText={tab === 'TODAS' ? 'Sin fichas.' : 'Arrastra una ficha aquí.'}
                    />
                )}
            </div>

            <SolicitudFormModal
                isOpen={formOpen}
                onClose={() => setFormOpen(false)}
                db={db}
                storage={storage}
                user={user}
                clients={clients}
                onClientCreated={(c) => setClients((prev) => [...prev, c].sort((a, b) => (a.name || '').localeCompare(b.name || '')))}
            />
            {fichaAbierta && (
                <FichaModal
                    ficha={fichas.find((f) => f.id === fichaAbierta.id) || fichaAbierta}
                    job={jobsById[fichaAbierta.jobId]}
                    machines={machines}
                    db={db}
                    user={user}
                    cutBeforePrint={cutBeforePrintFor(fichaAbierta.jobId, fichas, machines)}
                    onClose={() => setFichaAbierta(null)}
                    onOpenJob={abrirJob}
                />
            )}
            {convertir && (
                <ConvertirModal
                    solicitud={convertir}
                    onClose={() => setConvertir(null)}
                    onConvert={handleConvert}
                    converting={converting}
                />
            )}
        </div>
    );
};

export default WorkQueueView;
